import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createPrescription,
    fetchPrescriptions,
    fetchRecentPrescriptions,
    fetchPrescriptionByXID,
    updatePrescription,
    sharePrescriptionOnWhatsapp,
    fetchOrdersByXID,
} from "../../../redux/Thunk/Prescription/PrescriptionThunks";
import {
    clearLastCreatedPrescription,
    clearCurrentPrescription,
    setCurrentPrescription,
    setPrescriptionLoading,
} from "../../../redux/Reducer/Prescription/PrescriptionSlice";
import Button from "../../common/Button";
import Input from "../../common/Input";
import Download from "../../common/icons/Download";
import Receipt from "../../common/icons/Receipt";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import PatientHealthRecords, { INITIAL_RECORDS } from "./PatientHealthRecords";
import { foodToggleValues } from "../../../utils/constants";



const initialProductState = {
    productId: "", IID: "", isCustom: false, productName: "", units: 0, rxDays: 0, rxDaysDisplay: "",
    orderQty: 0, rxUnits: "", orderUnits: 0, balanceUnits: "", dosageNote: "", mrp: "",
    discount: "", gst: "", netAmt: "", type: "", typeIndex: "", morningTime: 0, midDay: 0,
    eveningTime: 0, night: 0, takenTime: "A/F", takenTimeIndex: "", extra: 0, extraIndex: 0,
    drugs: [], qty: 0, uom: 0, lookupMargin: { type: "" },
};

const PrescriptionMaker = forwardRef(({
    values,
    setFieldValue,
    handleSubmit,
    selectedFile,
    autoSignature,
    is_pin_b,
    rxCreateAccess,
    rxEditAccess,
    rxListAccess,
    generateRxCardAccess,
    rxHealthRecordAccess,
    handleUpload,
    file,
    medicineDetailSlot,
    billingDetailSlot,
    uploadPrescriptionSlot,
    submitOrderSlot,
    setBillingData,
    generateOrderAccess,
    onTabChange,
    onPrescriptionLoaded,
}, ref) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState("create");
    const [isCreated, setIsCreated] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);

    // Prescription-specific fields
    const [healthRecords, setHealthRecords] = useState(INITIAL_RECORDS);
    const [diagnosticsNotes, setDiagnosticsNotes] = useState("");
    const [nextVisit, setNextVisit] = useState("");
    const [nextVisitDate, setNextVisitDate] = useState(null);
    const [referredBy, setReferredBy] = useState("");
    const [referredTo, setReferredTo] = useState("");

    // Open/Edit prescription search
    const [xidSearch, setXidSearch] = useState("");
    const [showXidDropdown, setShowXidDropdown] = useState(false);
    const xidDropdownRef = useRef(null);

    // Track if data was edited after prescription creation (for smart XID reuse)
    const [dataSnapshot, setDataSnapshot] = useState(null);

    // Per-tab data isolation snapshots
    const tabSnapshots = useRef({ create: null, open: null, edit: null });

    // Orders linked to current prescription (for Open tab)
    const [prescriptionOrders, setPrescriptionOrders] = useState([]);
    const [lastSavedAt, setLastSavedAt] = useState(null);

    const doctorDetails = useSelector((state) => state?.doctor?.doctorDetails?.doctor);
    const prescriptionState = useSelector((state) => state?.prescription);
    const {
        prescriptions,
        pagination,
        lastCreatedPrescription,
        recentPrescriptions,
        currentPrescription,
        loading,
    } = prescriptionState || {};

    const DID = doctorDetails?.DID;
    const doctorName = `${doctorDetails?.personalInfo?.name || ""} ${doctorDetails?.personalInfo?.surname || ""}`.trim();

    // Save current tab's form data to snapshot
    const saveTabSnapshot = (tab) => {
        tabSnapshots.current[tab] = {
            products: JSON.parse(JSON.stringify(values?.products || [initialProductState])),
            returnProducts: JSON.parse(JSON.stringify(values?.returnProducts || [])),
            patient: JSON.parse(JSON.stringify(values?.patient || {})),
            billing: JSON.parse(JSON.stringify(values?.billing || {})),
            delivery: JSON.parse(JSON.stringify(values?.delivery || {})),
            payment: values?.payment || "upi",
            diagnosticsNotes, nextVisit, nextVisitDate, referredBy, referredTo,
            healthRecords: JSON.parse(JSON.stringify(healthRecords)),
            xidSearch, isCreated, dataSnapshot,
            // Save currentPrescription per tab so "Loaded/Editing" badge shows correct XID
            currentPrescription: currentPrescription ? JSON.parse(JSON.stringify(currentPrescription)) : null,
            prescriptionOrders: [...prescriptionOrders],
        };
    };

    // Restore form data from a tab's snapshot (or reset to blank)
    const restoreTabSnapshot = (tab) => {
        const snap = tabSnapshots.current[tab];
        if (snap) {
            setFieldValue("products", snap.products);
            setFieldValue("returnProducts", snap.returnProducts || []);
            setFieldValue("patient", snap.patient);
            setFieldValue("billing", snap.billing);
            setFieldValue("delivery", snap.delivery);
            setFieldValue("payment", snap.payment);
            setDiagnosticsNotes(snap.diagnosticsNotes || "");
            setNextVisit(snap.nextVisit || "");
            setNextVisitDate(snap.nextVisitDate || null);
            setReferredBy(snap.referredBy || "");
            setReferredTo(snap.referredTo || "");
            setHealthRecords(snap.healthRecords || INITIAL_RECORDS);
            setXidSearch(snap.xidSearch || "");
            setIsCreated(snap.isCreated || false);
            setDataSnapshot(snap.dataSnapshot || null);
            // Restore per-tab currentPrescription so "Loaded/Editing" shows correct XID
            dispatch(setCurrentPrescription(snap.currentPrescription || null));
            setPrescriptionOrders(snap.prescriptionOrders || []);
        } else {
            // Reset to blank state for this tab
            setFieldValue("products", [{ ...initialProductState }]);
            setFieldValue("returnProducts", []);
            setFieldValue("patient", { phoneNumber: "", name: "", surname: "", gender: "", dateofbirth: "", age: "", pincode: "", city: "" });
            setFieldValue("billing", { subTotal: 0, totalDiscount: 0, handlingFee: 0, smallCartFee: 0, deliveryCharges: 0, gst: 0, payableAmount: 0 });
            setFieldValue("delivery", { isAddress: false, isCollect: false, isClinic: true });
            setFieldValue("payment", "upi");
            setDiagnosticsNotes("");
            setNextVisit("");
            setNextVisitDate(null);
            setReferredBy("");
            setReferredTo("");
            setHealthRecords(INITIAL_RECORDS);
            setXidSearch("");
            setIsCreated(false);
            setDataSnapshot(null);
            dispatch(clearCurrentPrescription());
            setPrescriptionOrders([]);
        }
    };

    // Tab switch handler with data isolation
    const handleTabSwitch = (newTab) => {
        if (newTab === activeTab) return;
        // Save current tab snapshot
        saveTabSnapshot(activeTab);
        // Restore or reset new tab (also restores per-tab currentPrescription)
        restoreTabSnapshot(newTab);
        setActiveTab(newTab);
        if (onTabChange) onTabChange(newTab);
    };

    // Ensure activeTab is always visible. If hidden by permissions, select the first allowed tab.
    useEffect(() => {
        const allowedTabs = [
            { key: "create", visible: is_pin_b || rxCreateAccess !== false },
            { key: "open", visible: true },
            { key: "edit", visible: is_pin_b || rxEditAccess !== false },
            { key: "list", visible: is_pin_b || rxListAccess !== false }
        ].filter(t => t.visible).map(t => t.key);

        if (!allowedTabs.includes(activeTab) && allowedTabs.length > 0) {
            setActiveTab(allowedTabs[0]);
        }
    }, [is_pin_b, rxCreateAccess, rxEditAccess, rxListAccess, activeTab]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        getXIDForOrder,
        resetAfterOrder: () => {
            setIsCreated(false);
            setDataSnapshot(null);
        },
        setActiveTab: handleTabSwitch,
        handleOpenPrescription,
        getCurrentPrescription: () => currentPrescription,
    }));

    // Ensure loading state is reset on mount (in case of stale persisted state)
    useEffect(() => {
        dispatch(setPrescriptionLoading(false));
    }, [dispatch]);

    // Fetch prescriptions on tab switch or page change
    useEffect(() => {
        if (activeTab === "list" && DID) {
            dispatch(fetchPrescriptions({ DID, page: currentPage, limit: 5, search: searchTerm }));
        }
    }, [activeTab, currentPage, DID, dispatch, searchTerm]);

    // Fetch recent prescriptions for Open/Edit tabs
    useEffect(() => {
        if ((activeTab === "open" || activeTab === "edit") && DID) {
            dispatch(fetchRecentPrescriptions({ DID }));
        }
    }, [activeTab, DID, dispatch]);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (xidDropdownRef.current && !xidDropdownRef.current.contains(e.target)) {
                setShowXidDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Debounced search for prescription list
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((term) => {
            if (DID) {
                setCurrentPage(1);
                dispatch(fetchPrescriptions({ DID, page: 1, limit: 5, search: term }));
            }
        }, 400),
        [DID, dispatch]
    );

    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        debouncedSearch(val);
    };

    // Build prescription data from current form
    const buildPrescriptionData = () => {
        const activeProducts = values?.products || [];
        return {
            DID,
            patientPhoneNumber: values?.patient?.phoneNumber,
            patientName: values?.patient?.name,
            patientSurname: values?.patient?.surname || "",
            patientGender: values?.patient?.gender || "",
            patientDob: values?.patient?.dateofbirth || "",
            patientAge: values?.patient?.age || "",
            patientPincode: values?.patient?.pincode || "",
            patientCity: values?.patient?.city || "",
            diagnosticsNotes,
            nextVisit,
            nextVisitDate: nextVisitDate ? nextVisitDate.toISOString() : null,
            referredBy,
            referredTo,
            ...healthRecords,
            products: activeProducts.map(p => ({
                productName: p.productName,
                type: p.type || "",
                drugs: p.drugs || [],
                morningTime: p.morningTime || 0,
                midDay: p.midDay || 0,
                eveningTime: p.eveningTime || 0,
                night: p.night || 0,
                takenTime: p.takenTime || "A/F",
                rxDays: p.rxDays || 0,
                dosageNote: p.dosageNote || "",
                orderQty: p.orderQty || 0,
                mrp: p.mrp || "",
                IID: p.IID || p.productId || "",
                dosageMode: p.dosageMode || (p.isSOS ? "SOS" : p.isNA ? "NA" : p.isReducingDose ? "REDUCING" : p.isPeriodic ? "PERIODIC" : "REGULAR"),
                isSOS: p.isSOS || false,
                isNA: p.isNA || false,
                isReducingDose: p.isReducingDose || false,
                isPeriodic: p.isPeriodic || false,
                periodicFrequency: p.periodicFrequency || "",
                reducingDoses: p.reducingDoses || [],
            })),
        };
    };

    // Create a data snapshot to compare later
    const createDataSnapshot = () => {
        return JSON.stringify(buildPrescriptionData());
    };

    // Check if data matches the snapshot
    const hasDataChanged = () => {
        if (!dataSnapshot) return false;
        return createDataSnapshot() !== dataSnapshot;
    };

    // Validate prescription (without payment/delivery)
    const validateForPrescription = () => {
        if (!values?.patient?.phoneNumber?.length) return "Please select a patient!";
        if (!values?.patient?.name?.length) return "Please enter patient name!";

        const activeProducts = values?.products || [];
        if (activeProducts.length === 0) return "Please prescribe at least one product!";

        for (const product of activeProducts) {
            if (!product.productName) return "Product name is required!";
            const isSpecialMode = product.isSOS || product.isNA || product.isReducingDose || product.isPeriodic || product.dosageMode === 'SOS' || product.dosageMode === 'NA' || product.dosageMode === 'REDUCING' || product.dosageMode === 'PERIODIC';
            const hasSlotDose = product.morningTime > 0 || product.midDay > 0 || product.eveningTime > 0 || product.night > 0;
            if (!isSpecialMode && !hasSlotDose) {
                return `Please prescribe daily dosage for ${product.productName}!`;
            }
            const isNaDuration = product.isNA || product.dosageMode === 'NA' || product.rxDaysDisplay === 'NA';
            if (!isNaDuration && (!product.rxDays || product.rxDays <= 0 || isNaN(product.rxDays))) {
                return `Please enter RX days for ${product.productName}!`;
            }
        }
        return null;
    };

    // Check if prescription form is ready (for green button — without payment/delivery)
    const isPrescriptionFormReady = () => {
        const hasPatient = values?.patient?.phoneNumber?.length > 0 && values?.patient?.name?.length > 0;
        const activeProducts = values?.products || [];
        if (activeProducts.length === 0) return false;
        const allProductsValid = activeProducts.every(p => {
            if (!p.productName?.length) return false;
            const isSpecialMode = p.isSOS || p.isNA || p.isReducingDose || p.isPeriodic || p.dosageMode === 'SOS' || p.dosageMode === 'NA' || p.dosageMode === 'REDUCING' || p.dosageMode === 'PERIODIC';
            const hasSlotDose = (p.morningTime || 0) > 0 || (p.midDay || 0) > 0 || (p.eveningTime || 0) > 0 || (p.night || 0) > 0;
            const isNaDuration = p.isNA || p.dosageMode === 'NA' || p.rxDaysDisplay === 'NA';
            const isDurationValid = isNaDuration ? true : (p.rxDays > 0 && !isNaN(p.rxDays));
            return (isSpecialMode || hasSlotDose) && isDurationValid;
        });
        return hasPatient && allProductsValid;
    };

    // Handle Create Prescription
    const handleCreatePrescription = async () => {
        const error = validateForPrescription();
        if (error) {
            toast.error(error);
            return;
        }

        const prescriptionData = buildPrescriptionData();
        const response = await dispatch(createPrescription(prescriptionData)).unwrap();
        if (response?.status === 201) {
            setIsCreated(true);
            setDataSnapshot(JSON.stringify(prescriptionData));
            
            // Update Formik PID if returned
            if (response.data?.prescription?.PID) {
                setFieldValue("patient.PID", response.data.prescription.PID);
            }
            setLastSavedAt(Date.now());
        }
    };

    // Handle Close (after creation)
    const handleClose = () => {
        setIsCreated(false);
        setDataSnapshot(null);
        dispatch(clearLastCreatedPrescription());
    };

    // Get XID for order (smart reuse logic)
    const getXIDForOrder = async () => {
        // Case 0: Open tab with a loaded prescription — always reuse the opened XID
        if (activeTab === "open" && currentPrescription?.XID) {
            return currentPrescription.XID;
        }

        // Case 1: Prescription already created and data hasn't changed — reuse XID
        if (lastCreatedPrescription?.XID && !hasDataChanged()) {
            return lastCreatedPrescription.XID;
        }

        // Case 2: Prescription created but data changed — ask for confirmation
        if (lastCreatedPrescription?.XID && hasDataChanged()) {
            const confirmed = window.confirm(
                `The prescription data has been modified since ${lastCreatedPrescription.XID} was generated. Would you like to create a new prescription for this order?\n\nYes → A new prescription ID will be generated\nNo → Order will not be placed`
            );
            if (!confirmed) {
                return null; // User cancelled
            }
            // Create new prescription
            const prescriptionData = buildPrescriptionData();
            const response = await dispatch(createPrescription(prescriptionData)).unwrap();
            if (response?.status === 201) {
                // Update Formik PID if returned
                if (response.data?.prescription?.PID) {
                    setFieldValue("patient.PID", response.data.prescription.PID);
                }
                return response.data.prescription.XID;
            }
            return null;
        }

        // Case 3: No prescription created yet — auto-create
        const prescriptionData = buildPrescriptionData();
        const error = validateForPrescription();
        if (error) {
            // If prescription can't be validated, proceed without XID (legacy behavior)
            return undefined;
        }
        const response = await dispatch(createPrescription(prescriptionData)).unwrap();
        if (response?.status === 201) {
            // Update Formik PID if returned
            if (response.data?.prescription?.PID) {
                setFieldValue("patient.PID", response.data.prescription.PID);
            }
            return response.data.prescription.XID;
        }
        return undefined;
    };

    // Open prescription from XID (for Open/Edit tabs)
    const handleOpenPrescription = async (xid) => {
        if (!xid) return;
        const response = await dispatch(fetchPrescriptionByXID({ xid })).unwrap();
        if (response?.prescription) {
            const rx = response.prescription;
            // Auto-fill form
            setFieldValue("patient.phoneNumber", rx.patientPhoneNumber || "");
            setFieldValue("patient.name", rx.patientName || "");
            setFieldValue("patient.surname", rx.patientSurname || "");
            setFieldValue("patient.gender", rx.patientGender || "");
            setFieldValue("patient.dateofbirth", rx.patientDob || "");
            setFieldValue("patient.age", rx.patientAge || "");
            setFieldValue("patient.pincode", rx.patientPincode || "");
            setFieldValue("patient.city", rx.patientCity || "");
            setFieldValue("patient.PID", rx.PID || "");

            // Set prescription-specific fields
            setDiagnosticsNotes(rx.diagnosticsNotes || "");
            setReferredBy(rx.referredBy || "");
            setReferredTo(rx.referredTo || "");

            setHealthRecords({
                notes: rx.notes || "",
                observations: rx.observations || "",
                treatments: rx.treatments || [],
                visitCount: rx.visitCount || 0,
                complaints: rx.complaints || [],
                diagnosis: rx.diagnosis || [],
                testsPrescribed: rx.testsPrescribed || [],
                measurements: rx.measurements || [],
                advice: rx.advice || [],
                diagrams: rx.diagrams || [],
                images: rx.images || [],
            });

            if (rx.nextVisit) {
                setNextVisit(rx.nextVisit);
                setNextVisitDate(rx.nextVisitDate ? new Date(rx.nextVisitDate) : null);
            } else {
                setNextVisit("");
                setNextVisitDate(null);
            }

            // Set products with properly computed fields
            let initialProducts = [];
            if (rx.prescriptionProducts && rx.prescriptionProducts.length > 0) {
                initialProducts = rx.prescriptionProducts.map(p => {
                    const rxDays = Number(p.rxDays) || 0;
                    const morningTime = Number(p.morningTime) || 0;
                    const midDay = Number(p.midDay) || 0;
                    const eveningTime = Number(p.eveningTime) || 0;
                    const night = Number(p.night) || 0;
                    const totalTimes = morningTime + midDay + eveningTime + night;
                    const rxUnits = rxDays * totalTimes;
                    const isCustom = p.isCustom ?? (p.IID && String(p.IID).startsWith("CP"));
                    const orderQty = isCustom ? 0 : (Number(p.orderQty) || 0);
                    // Build rxDaysDisplay from rxDays
                    let rxDaysDisplay = "";
                    const daysMap = [
                        { label: "1 year", numeric: 365 },
                        { label: "9 months", numeric: 270 },
                        { label: "6 months", numeric: 180 },
                        { label: "3 months", numeric: 90 },
                        { label: "2 months", numeric: 60 },
                        { label: "1 month", numeric: 30 },
                        { label: "3 weeks/21 days", numeric: 21 },
                        { label: "2 weeks/14 days", numeric: 14 },
                        { label: "1 week/7 days", numeric: 7 },
                    ];
                    const found = daysMap.find(d => d.numeric === rxDays);
                    rxDaysDisplay = found ? found.label : (rxDays > 0 ? String(rxDays) : "");

                    return {
                        productId: p.IID || "",
                        IID: p.IID || "",
                        isCustom,
                        productName: p.productName || "",
                        units: Number(p.units) || 0,
                        rxDays,
                        rxDaysDisplay,
                        orderQty,
                        rxUnits: rxUnits || "",
                        orderUnits: orderQty,
                        balanceUnits: Math.max(0, rxUnits - orderQty),
                        dosageNote: p.dosageNote || "",
                        mrp: Number(p.mrp) || 0,
                        discount: Number(p.discount) || 0,
                        gst: Number(p.gst) || 0,
                        netAmt: Number(p.netAmt) || 0,
                        type: p.type || "",
                        typeIndex: "",
                        morningTime,
                        midDay,
                        eveningTime,
                        night,
                        takenTime: p.takenTime || "A/F",
                        takenTimeIndex: foodToggleValues.indexOf(p.takenTime || "A/F") !== -1 ? foodToggleValues.indexOf(p.takenTime || "A/F") : 0,
                        extra: 0,
                        extraIndex: 0,
                        drugs: p.drugs || [],
                        qty: Number(p.qty) || 0,
                        uom: p.uom || 0,
                        lookupMargin: { type: p.lookupMarginType || "" },
                        dosageMode: p.dosageMode || (p.isSOS ? "SOS" : p.isNA ? "NA" : p.isReducingDose ? "REDUCING" : p.isPeriodic ? "PERIODIC" : "REGULAR"),
                        isSOS: p.isSOS || false,
                        isNA: p.isNA || false,
                        isReducingDose: p.isReducingDose || false,
                        isPeriodic: p.isPeriodic || false,
                        periodicFrequency: p.periodicFrequency || "",
                        reducingDoses: p.reducingDoses || [],
                    };
                });
                setFieldValue("products", initialProducts);
                // Trigger billing recalculation after products are loaded
                if (setBillingData) setBillingData(0);
            }

            setXidSearch(xid);

            // Fetch orders linked to this prescription XID (for Open tab note & return products)
            try {
                const ordersResult = await dispatch(fetchOrdersByXID({ xid })).unwrap();
                const orders = ordersResult?.orders || [];
                setPrescriptionOrders(orders);

                // Populate returnProducts from linked order if available
                let returnProds = [];
                if (orders.length > 0) {
                    const orderWithReturn = orders.find(o => o.orderProducts?.some(p => p.isReturn)) || orders[0];
                    if (orderWithReturn?.orderProducts) {
                        returnProds = orderWithReturn.orderProducts
                            .filter(p => p.isReturn)
                            .map(p => ({
                                productId: p.IID || "",
                                IID: p.IID || "",
                                productName: p.productName || "",
                                orderQty: Number(p.orderQty) || 1,
                                mrp: Number(p.mrp) || 0,
                                discount: Number(p.discount) || 0,
                                gst: Number(p.gst) || 0,
                                netAmt: Number(p.netAmt) || 0,
                                type: p.type || "Tablet",
                                isReturn: true,
                            }));
                    }
                }
                setFieldValue("returnProducts", returnProds);
            } catch {
                setPrescriptionOrders([]);
                setFieldValue("returnProducts", []);
            }

            // Take a snapshot of the loaded data for "Detect Edits" logic
            const snapshotData = {
                DID: rx.DID,
                patientPhoneNumber: rx.patientPhoneNumber || "",
                patientName: rx.patientName || "",
                patientSurname: rx.patientSurname || "",
                patientGender: rx.patientGender || "",
                patientDob: rx.patientDob || "",
                patientAge: rx.patientAge || "",
                patientPincode: rx.patientPincode || "",
                patientCity: rx.patientCity || "",
                diagnosticsNotes: rx.diagnosticsNotes || "",
                nextVisit: rx.nextVisit || "",
                nextVisitDate: rx.nextVisitDate ? new Date(rx.nextVisitDate).toISOString() : null,
                referredBy: rx.referredBy || "",
                referredTo: rx.referredTo || "",
                notes: rx.notes || "",
                observations: rx.observations || "",
                treatments: rx.treatments || [],
                visitCount: rx.visitCount || 0,
                complaints: rx.complaints || [],
                diagnosis: rx.diagnosis || [],
                testsPrescribed: rx.testsPrescribed || [],
                measurements: rx.measurements || [],
                advice: rx.advice || [],
                products: initialProducts.map(p => ({
                    productName: p.productName,
                    type: p.type || "",
                    drugs: p.drugs || [],
                    morningTime: p.morningTime || 0,
                    midDay: p.midDay || 0,
                    eveningTime: p.eveningTime || 0,
                    night: p.night || 0,
                    takenTime: p.takenTime || "A/F",
                    rxDays: p.rxDays || 0,
                    dosageNote: p.dosageNote || "",
                    orderQty: p.orderQty || 0,
                    mrp: p.mrp || "",
                    dosageMode: p.dosageMode || "REGULAR",
                    isSOS: p.isSOS || false,
                    isNA: p.isNA || false,
                    isReducingDose: p.isReducingDose || false,
                    isPeriodic: p.isPeriodic || false,
                    periodicFrequency: p.periodicFrequency || "",
                    reducingDoses: p.reducingDoses || [],
                })),
                images: rx.images || [],
            };
            setDataSnapshot(JSON.stringify(snapshotData));
            if (onPrescriptionLoaded) onPrescriptionLoaded(rx);
        }
    };

    // Handle Update Prescription
    const handleUpdatePrescription = async () => {
        if (!currentPrescription?.XID) {
            toast.error("No prescription loaded to update");
            return;
        }

        const error = validateForPrescription();
        if (error) {
            toast.error(error);
            return;
        }

        const prescriptionData = buildPrescriptionData();
        const response = await dispatch(updatePrescription({ xid: currentPrescription.XID, data: prescriptionData })).unwrap();
        if (response?.success) {
            setLastSavedAt(Date.now());
        }
    };

    // Download prescription PDF
    const handleDownload = async (pdfUrl, patientName) => {
        try {
            const res = await fetch(pdfUrl);
            const blob = await res.blob();
            const pdfBlob = new Blob([blob], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(pdfBlob);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = `${patientName || "prescription"}_prescription.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (error) {
            toast.error("Failed to download prescription");
        }
    };

    // Share on WhatsApp
    const handleShare = async (prescription) => {
        const shareData = {
            XID: prescription.XID,
            patientPhoneNumber: prescription.patientPhoneNumber,
            patientName: `${prescription.patientName} ${prescription.patientSurname || ""}`.trim(),
            doctorName: doctorName,
            pdfUrl: prescription.pdfUrl,
        };
        await dispatch(sharePrescriptionOnWhatsapp(shareData));
    };

    // Print prescription
    const handlePrint = async (pdfUrl) => {
        try {
            const res = await fetch(pdfUrl);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl);
            if (printWindow) {
                printWindow.addEventListener("load", () => {
                    printWindow.print();
                });
            }
        } catch (error) {
            toast.error("Failed to print prescription");
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    // Open action button — switch to Open Prescription tab with the XID
    const handleOpenAction = (xid) => {
        handleTabSwitch("open");
        handleOpenPrescription(xid);
    };

    const prescriptionFormReady = isPrescriptionFormReady();

    const isPatientFilled = values?.patient?.phoneNumber?.length >= 10 &&
        values?.patient?.name?.trim()?.length > 0 &&
        values?.patient?.surname?.trim()?.length > 0 &&
        (values?.patient?.dateofbirth || String(values?.patient?.age)?.trim()?.length > 0) &&
        values?.patient?.gender &&
        (String(values?.patient?.pincode)?.length === 6 || values?.patient?.city?.trim()?.length > 0);
    const showBlocks = (activeTab === "create" && isPatientFilled) || ((activeTab === "open" || activeTab === "edit") && currentPrescription);

    return (
        <div className="border border-primary-100 p-4 rounded-[8px]">
            {/* Mobile Title Tile */}
            <div className="flex sm:hidden items-center gap-2 px-4 py-2 bg-white border border-primary-100 rounded-[8px] mb-3 shadow-sm justify-center">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#e62121" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM6 20V4h5v7h7v9H6z" fill="#e62121" /></svg>
                <h3 className="text-[15px] font-bold text-secondry-850 tracking-wide uppercase">
                    Prescription
                </h3>
            </div>

            {/* ═══ 1. Header (Tabs) — always at top ═══ */}
            <div className="flex items-center p-1 bg-primary-50 border-[0.5px] border-primary-100 rounded-[8px] mb-4 w-full shadow-sm relative">
                {/* Desktop Title */}
                <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white rounded-[4px] mr-1">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#e62121" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM6 20V4h5v7h7v9H6z" fill="#e62121" /></svg>
                    <h3 className="text-[15px] font-semibold text-secondry-600 whitespace-nowrap uppercase">
                        Prescription
                    </h3>
                </div>
                <div className="hidden sm:block w-[1px] h-5 bg-primary-100 mx-1"></div>
                {[
                    { key: "create", label: "Create" },
                    { key: "open", label: "Open" },
                    { key: "edit", label: "Edit" },
                    { key: "list", label: "List" },
                ].filter(tab => {
                    // PIN-A users: hide tabs when access toggle is OFF
                    if (is_pin_b) return true; // PIN-B has full access
                    if (tab.key === "create" && !rxCreateAccess) return false;
                    if (tab.key === "edit" && !rxEditAccess) return false;
                    if (tab.key === "list" && !rxListAccess) return false;
                    return true;
                }).map((tab, idx, arr) => (
                    <React.Fragment key={tab.key}>
                        <button
                            type="button"
                            onClick={() => handleTabSwitch(tab.key)}
                            className={`flex-1 relative py-2 text-[13px] font-medium rounded-[8px] transition-all flex items-center justify-center ${activeTab === tab.key
                                ? "text-primary-700"
                                : "text-secondry-600 hover:text-primary-700 hover:bg-white"
                                }`}
                        >
                            {tab.label}
                            {activeTab === tab.key && (
                                <div className="absolute bottom-[-4px] left-1/2 -translate-x-1/2 w-[50%] h-[3px] bg-primary-600 rounded-t-[4px]" />
                            )}
                        </button>
                        {idx < arr.length - 1 && <div className="w-[1px] h-4 bg-primary-100 mx-1"></div>}
                    </React.Fragment>
                ))}
            </div>

            {/* ═══ Tab: Open — XID search (before MedicineDetail) ═══ */}
            {activeTab === "open" && (
                <div className="mb-3 relative" ref={xidDropdownRef}>
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <input type="text" value={xidSearch} onChange={(e) => { setXidSearch(e.target.value.toUpperCase()); setShowXidDropdown(true); }} onFocus={() => setShowXidDropdown(true)} placeholder="Search or Enter Prescription Id" className="w-full h-[42px] px-3 py-2 border border-primary-200 rounded-[8px] bg-white text-[13px] text-secondry-600 outline-none focus:border-primary-600 transition-all" />
                            {showXidDropdown && recentPrescriptions?.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-primary-200 rounded-[8px] shadow-lg max-h-[250px] overflow-y-auto">
                                    {recentPrescriptions.filter(p => !xidSearch || p.XID.includes(xidSearch) || p.patientName.toLowerCase().includes(xidSearch.toLowerCase())).map(p => (
                                        <div key={p.XID} onClick={() => { handleOpenPrescription(p.XID); setShowXidDropdown(false); }} className="px-3 py-2.5 hover:bg-primary-50 cursor-pointer transition-all border-b border-primary-50 last:border-b-0">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[13px] font-medium text-secondry-600">{p.XID}</span>
                                                <span className="text-[11px] text-primary-400">{formatDate(p.createdAt)}</span>
                                            </div>
                                            <span className="text-[12px] text-primary-400">{p.patientName} {p.patientSurname || ""}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button type="button" onClick={() => { handleOpenPrescription(xidSearch); setShowXidDropdown(false); }} className="h-[42px] px-4 text-[13px] bg-primary-600 text-white rounded-[8px] hover:bg-primary-700 transition-all whitespace-nowrap">Open Prescription</Button>
                    </div>
                    {currentPrescription && (
                        <div className="flex items-center gap-2 mt-3 p-3 bg-primary-50 border border-primary-200 rounded-[8px]">
                            <span className="text-[13px] text-secondry-600 font-medium">Loaded: <span className="text-primary-700 font-semibold">{currentPrescription.XID}</span> — {currentPrescription.patientName} {currentPrescription.patientSurname || ""}</span>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ Tab: Edit — XID search (before MedicineDetail) ═══ */}
            {activeTab === "edit" && (
                <div className="mb-3 relative" ref={xidDropdownRef}>
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <input type="text" value={xidSearch} onChange={(e) => { setXidSearch(e.target.value.toUpperCase()); setShowXidDropdown(true); }} onFocus={() => setShowXidDropdown(true)} placeholder="Search or Enter Prescription Id" className="w-full h-[42px] px-3 py-2 border border-primary-200 rounded-[8px] bg-white text-[13px] text-secondry-600 outline-none focus:border-primary-600 transition-all" />
                            {showXidDropdown && recentPrescriptions?.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-primary-200 rounded-[8px] shadow-lg max-h-[250px] overflow-y-auto">
                                    {recentPrescriptions.filter(p => !xidSearch || p.XID.includes(xidSearch) || p.patientName.toLowerCase().includes(xidSearch.toLowerCase())).map(p => (
                                        <div key={p.XID} onClick={() => { handleOpenPrescription(p.XID); setShowXidDropdown(false); }} className="px-3 py-2.5 hover:bg-primary-50 cursor-pointer transition-all border-b border-primary-50 last:border-b-0">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[13px] font-medium text-secondry-600">{p.XID}</span>
                                                <span className="text-[11px] text-primary-400">{formatDate(p.createdAt)}</span>
                                            </div>
                                            <span className="text-[12px] text-primary-400">{p.patientName} {p.patientSurname || ""}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <Button type="button" onClick={() => { handleOpenPrescription(xidSearch); setShowXidDropdown(false); }} className="h-[42px] px-4 text-[13px] bg-primary-600 text-white rounded-[8px] hover:bg-primary-700 transition-all whitespace-nowrap">Load Prescription</Button>
                    </div>
                    {currentPrescription && (
                        <div className="flex items-center gap-2 mt-3 p-3 bg-primary-50 border border-primary-200 rounded-[8px]">
                            <span className="text-[13px] text-secondry-600 font-medium">Editing: <span className="text-primary-700 font-semibold">{currentPrescription.XID}</span> — {currentPrescription.patientName} {currentPrescription.patientSurname || ""}</span>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ Empty Create State Block ═══ */}
            {activeTab === "create" && !isPatientFilled && (
                <div className="w-full flex items-center justify-center border border-dashed border-primary-200 bg-primary-50 rounded-[8px] h-[100px] mt-4">
                    <p className="text-[13px] text-primary-400 font-medium flex items-center gap-2">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="#9CA3AF" /></svg>
                        Enter Patient Details to Create
                    </p>
                </div>
            )}

            {/* ═══ 2. Patient Health Records (Modular) ═══ */}
            {showBlocks && (is_pin_b || rxHealthRecordAccess !== false) && (
                <PatientHealthRecords
                    readOnly={activeTab === "open"}
                    data={healthRecords}
                    onChange={setHealthRecords}
                    diagnosticsNotes={diagnosticsNotes} setDiagnosticsNotes={setDiagnosticsNotes}
                    nextVisit={nextVisit} setNextVisit={setNextVisit}
                    nextVisitDate={nextVisitDate} setNextVisitDate={setNextVisitDate}
                    referredBy={referredBy} setReferredBy={setReferredBy}
                    referredTo={referredTo} setReferredTo={setReferredTo}
                    DID={DID}
                    lastSavedAt={lastSavedAt}
                />
            )}

            {/* ═══ 3. MedicineDetail — on create always, on open/edit only when prescription loaded ═══ */}
            {showBlocks && (
                activeTab === "open" && currentPrescription
                    ? React.cloneElement(medicineDetailSlot, { readOnly: true, activeTab, qtyEditable: true })
                    : React.cloneElement(medicineDetailSlot, { activeTab })
            )}

            {/* ═══ 4. BillingDetail — on create always, on open/edit only when prescription loaded ═══ */}
            {showBlocks && (!is_pin_b || generateOrderAccess !== false) && (
                <div className="flex flex-wrap">
                    {activeTab === "open" && currentPrescription
                        ? React.cloneElement(billingDetailSlot, { readOnly: true })
                        : billingDetailSlot}
                </div>
            )}

            {showBlocks && (
                <div className="flex flex-wrap">
                    {/* ═══ 5. Upload + Generate Prescription + Submit Order (Create tab) ═══ */}
                    {activeTab === "create" && (
                        <div className="w-full p-2 rounded-[8px] bg-primary-50 mt-2 flex flex-col md:flex-row gap-2">
                            {uploadPrescriptionSlot && !autoSignature && <div className="flex-1">{uploadPrescriptionSlot}</div>}

                            {/* Block A: Generate Prescription */}
                            {(is_pin_b || generateRxCardAccess) && (
                                <div className="flex-1 border border-primary-100 p-4 rounded-[8px] bg-white flex flex-col justify-between">
                                    <div className="flex-1">
                                        <h4 className="text-[13px] text-center font-semibold text-secondry-600 mb-3 uppercase">Generate Prescription</h4>
                                        {isCreated && lastCreatedPrescription && (
                                            <div className="flex items-center gap-2 mb-3 p-3 bg-success-50 border border-success-200 rounded-[8px]">
                                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM8 15L3 10L4.41 8.59L8 12.17L15.59 4.58L17 6L8 15Z" fill="#00C732" />
                                                </svg>
                                                <span className="text-[13px] text-success-700 font-medium">
                                                    Successfully generated prescription with Id {lastCreatedPrescription.XID}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-4">
                                        {!isCreated ? (
                                            <Button type="button" onClick={handleCreatePrescription} disabled={loading}
                                                className={`flex text-[13px] items-center justify-center gap-2 w-full p-2 rounded-[8px] border-[2px] transition-all ${prescriptionFormReady ? "text-white bg-success-600 border-success-600 hover:bg-success-700" : "text-white bg-primary-600 border-primary-600 hover:bg-primary-700"}`}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff" xmlns="http://www.w3.org/2000/svg"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm-1 7V3.5L18.5 9H13zM6 20V4h5v7h7v9H6z" fill="#fff" /></svg>
                                                {loading ? "Generating..." : "Create Prescription"}
                                            </Button>
                                        ) : (
                                            <div>
                                                <Button type="button" onClick={handleClose} className="flex text-[13px] items-center justify-center gap-2 bg-primary-50 border border-primary-200 text-secondry-600 w-full p-2 rounded-[8px] mb-2 hover:bg-primary-100 transition-all">Close</Button>
                                                {lastCreatedPrescription?.pdfUrl && (
                                                    <div className="grid grid-cols-4 gap-2">
                                                        <Button type="button" onClick={() => handleOpenAction(lastCreatedPrescription.XID)} className="flex text-[12px] items-center justify-center gap-1 bg-[#F2EEEE] border border-primary-600 text-secondry-600 p-2 rounded-[8px] hover:bg-primary-100 transition-all">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="#513E3E" /></svg>
                                                            Open
                                                        </Button>
                                                        <Button type="button" onClick={() => handleDownload(lastCreatedPrescription.pdfUrl, lastCreatedPrescription.patientName)} className="flex text-[12px] items-center justify-center gap-1 bg-[#F2EEEE] border border-primary-600 text-secondry-600 p-2 rounded-[8px] hover:bg-primary-100 transition-all">
                                                            <Download width={16} height={16} />Download
                                                        </Button>
                                                        <Button type="button" onClick={() => handleShare(lastCreatedPrescription)} className="flex text-[12px] items-center justify-center gap-1 bg-[#F2EEEE] border border-success-600 text-success-700 p-2 rounded-[8px] hover:bg-success-50 transition-all">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#00C732" /><path d="M12 2C6.48 2 2 6.48 2 12c0 1.77.47 3.48 1.35 4.99L2 22l5.16-1.35C8.67 21.53 10.3 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.58 0-3.07-.42-4.35-1.15l-.31-.18-3.22.84.86-3.13-.2-.32A7.963 7.963 0 014 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8z" fill="#00C732" /></svg>
                                                            Share
                                                        </Button>
                                                        <Button type="button" onClick={() => handlePrint(lastCreatedPrescription.pdfUrl)} className="flex text-[12px] items-center justify-center gap-1 bg-[#F2EEEE] border border-primary-600 text-secondry-600 p-2 rounded-[8px] hover:bg-primary-100 transition-all">
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" fill="#513E3E" /></svg>
                                                            Print
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                    {/* Block B: Submit Order (Create) */}
                            {(!is_pin_b || generateOrderAccess !== false) && (
                                <div className="flex-1 border border-primary-100 p-4 rounded-[8px] bg-white flex flex-col justify-between">
                                    <div className="flex-1">
                                        <h4 className="text-[13px] text-center font-semibold text-secondry-600 uppercase">Generate Order</h4>
                                    </div>
                                    <div className="mt-2">
                                        {submitOrderSlot}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══ Submit Order card on Open tab ═══ */}
                    {activeTab === "open" && currentPrescription && (!is_pin_b || generateOrderAccess !== false) && (
                        <div className="w-full p-2 rounded-[8px] bg-primary-50 mt-2 flex flex-col gap-2">
                            <div className="border border-primary-100 p-4 rounded-[8px] bg-white flex flex-col justify-between">
                                <div className="flex-1">
                                    <h4 className="text-[13px] text-center font-semibold text-secondry-600 uppercase mb-3">Submit Order</h4>
                                    {prescriptionOrders.length > 0 && (
                                        <div className="flex flex-col gap-1 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-[8px]">
                                            <span className="text-[12px] text-amber-700 font-medium">
                                                ⚠ Order(s) with prescription #{currentPrescription.XID} placed earlier:
                                            </span>
                                            {prescriptionOrders.map((order) => (
                                                <span key={order.OID} className="text-[12px] text-amber-600">
                                                    • Order #{order.OID} — {formatDate(order.createdAt)}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-2">
                                    {submitOrderSlot}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ Tab: Edit — Update Prescription button ═══ */}
                    {activeTab === "edit" && currentPrescription && (
                        <div className="w-full mt-2">
                            <Button type="button" onClick={handleUpdatePrescription} disabled={loading || !hasDataChanged()}
                                className={`flex text-[13px] items-center justify-center gap-2 text-white border-[2px] w-full p-2 rounded-[8px] mb-2 transition-all ${prescriptionFormReady && hasDataChanged() ? "bg-success-600 border-success-600 hover:bg-success-700" : "bg-primary-600 border-primary-600 hover:bg-primary-700 opacity-50 cursor-not-allowed"}`}>
                                <Receipt width={16} height={16} fill="#fff" />
                                {loading ? "Updating..." : "Update Prescription"}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* ═══ Tab 4: Prescription List ═══ */}
            {activeTab === "list" && (
                <div>
                    <div className="mb-3">
                        <Input name="prescriptionSearch" value={searchTerm} onChange={handleSearchChange} placeholder="Search by XID, Contact, or Patient Name" type="text" className="border-none bg-transparent shadow-none outline-none placeholder:text-secondry-600 w-full h-[24px]" />
                    </div>
                    {/* Desktop View Table */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-left min-w-[500px]">
                            <thead>
                                <tr className="border-b border-primary-100">
                                    <th className="text-[11px] text-primary-400 font-medium py-2 px-2" style={{ textAlign: "left" }}>Date & ID</th>
                                    <th className="text-[11px] text-primary-400 font-medium py-2 px-2" style={{ textAlign: "left" }}>Patient Details</th>
                                    <th className="text-[11px] text-primary-400 font-medium py-2 px-2" style={{ textAlign: "left" }}>Medicines</th>
                                    <th className="text-[11px] text-primary-400 font-medium py-2 px-2" style={{ textAlign: "center" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody><tr style={{ height: "8px" }}><td colSpan="4"></td></tr></tbody>
                            <tbody>
                                {prescriptions && prescriptions.length > 0 ? (
                                    prescriptions.map((rx) => (
                                        <tr key={rx.XID} className="bg-primary-300 rounded-[8px]" style={{ borderBottom: "6px solid white" }}>
                                            <td className="text-[12px] text-secondry-600 py-3 px-2 whitespace-nowrap rounded-l-[8px]">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{formatDate(rx.createdAt)}</span>
                                                    <span className="text-[11px] text-primary-400">ID: {rx.XID}</span>
                                                </div>
                                            </td>
                                            <td className="text-[12px] text-secondry-600 py-3 px-2">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{rx.patientName} {rx.patientSurname || ""}</span>
                                                    <div className="flex items-center gap-2 text-[11px] text-primary-400">
                                                        <span>{rx.patientPhoneNumber}</span>
                                                        <span>•</span>
                                                        <span>{rx.patientGender?.charAt(0)} | {rx.patientAge || (rx.patientDob ? (new Date().getFullYear() - new Date(rx.patientDob).getFullYear()) : "-")}y</span>
                                                        {rx.patientCity && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="italic">{rx.patientCity}</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-[12px] text-secondry-600 py-3 px-2 max-w-[180px]">
                                                <span className="line-clamp-2">{rx.prescriptionProducts?.map(p => p.productName).join(", ") || "—"}</span>
                                            </td>
                                            <td className="py-3 px-2 rounded-r-[8px]">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button type="button" onClick={() => handleOpenAction(rx.XID)} className="p-2 rounded-md hover:bg-primary-100 transition-all" title="Open">
                                                        <svg width="24" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="#513E3E" /></svg>
                                                    </button>
                                                    <button type="button" onClick={() => handleDownload(rx.pdfUrl, rx.patientName)} className="p-2 rounded-md hover:bg-primary-100 transition-all" title="Download">
                                                        <Download width={24} height={16} />
                                                    </button>
                                                    <button type="button" onClick={() => handleShare(rx)} className="p-2 rounded-md hover:bg-primary-100 transition-all" title="Share on WhatsApp">
                                                        <svg width="24" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#00C732" /><path d="M12 2C6.48 2 2 6.48 2 12c0 1.77.47 3.48 1.35 4.99L2 22l5.16-1.35C8.67 21.53 10.3 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.58 0-3.07-.42-4.35-1.15l-.31-.18-3.22.84.86-3.13-.2-.32A7.963 7.963 0 014 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8z" fill="#00C732" /></svg>
                                                    </button>
                                                    <button type="button" onClick={() => handlePrint(rx.pdfUrl)} className="p-2 rounded-md hover:bg-primary-100 transition-all" title="Print">
                                                        <svg width="24" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" fill="#513E3E" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="text-center py-8 text-[13px] text-primary-400">{loading ? "Loading..." : "No prescriptions found"}</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View Cards */}
                    <div className="block md:hidden space-y-3">
                        {prescriptions && prescriptions.length > 0 ? (
                            prescriptions.map((rx) => (
                                <div key={rx.XID} className="bg-primary-50 border border-primary-100 rounded-[12px] p-3 shadow-sm flex flex-col gap-2">
                                    {/* Top row: ID and Date */}
                                    <div className="flex justify-between items-center border-b border-primary-100 pb-2">
                                        <span className="text-[12px] font-bold text-secondry-600">ID: {rx.XID}</span>
                                        <span className="text-[11px] font-medium text-primary-400">{formatDate(rx.createdAt)}</span>
                                    </div>
                                    {/* Middle: Patient details */}
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[13px] font-bold text-secondry-800">{rx.patientName} {rx.patientSurname || ""}</span>
                                        <span className="text-[11px] text-primary-400">
                                            {rx.patientPhoneNumber} • {rx.patientGender?.charAt(0)} | {rx.patientAge || (rx.patientDob ? (new Date().getFullYear() - new Date(rx.patientDob).getFullYear()) : "-")}y
                                            {rx.patientCity && ` • ${rx.patientCity}`}
                                        </span>
                                    </div>
                                    {/* Medicines */}
                                    <div className="bg-white border border-primary-50 rounded-[8px] p-2">
                                        <span className="text-[10px] text-primary-400 font-bold uppercase tracking-wider block mb-0.5">Medicines</span>
                                        <span className="text-[12px] text-secondry-600 line-clamp-3">
                                            {rx.prescriptionProducts?.map(p => p.productName).join(", ") || "—"}
                                        </span>
                                    </div>
                                    {/* Bottom Actions */}
                                    <div className="flex items-center justify-between mt-1 pt-2 border-t border-primary-100">
                                        <button type="button" onClick={() => handleOpenAction(rx.XID)} className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 bg-white border border-primary-200 px-2.5 py-1.5 rounded-[8px] hover:bg-primary-50 transition-all">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="currentColor" /></svg>
                                            Open
                                        </button>
                                        <div className="flex items-center gap-1.5">
                                            <button type="button" onClick={() => handleDownload(rx.pdfUrl, rx.patientName)} className="p-2 rounded-md bg-white border border-primary-200 text-secondry-600 hover:bg-primary-50 transition-all" title="Download">
                                                <Download width={16} height={16} />
                                            </button>
                                            <button type="button" onClick={() => handleShare(rx)} className="p-2 rounded-md bg-white border border-primary-200 hover:bg-primary-50 transition-all" title="Share on WhatsApp">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" fill="#00C732" /><path d="M12 2C6.48 2 2 6.48 2 12c0 1.77.47 3.48 1.35 4.99L2 22l5.16-1.35C8.67 21.53 10.3 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18c-1.58 0-3.07-.42-4.35-1.15l-.31-.18-3.22.84.86-3.13-.2-.32A7.963 7.963 0 014 12c0-4.41 3.59-8 8-8s8 3.59 8 8-3.59 8-8 8z" fill="#00C732" /></svg>
                                            </button>
                                            <button type="button" onClick={() => handlePrint(rx.pdfUrl)} className="p-2 rounded-md bg-white border border-primary-200 text-secondry-600 hover:bg-primary-50 transition-all" title="Print">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" fill="#513E3E" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[13px] text-primary-400">{loading ? "Loading..." : "No prescriptions found"}</div>
                        )}
                    </div>
                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <button type="button" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={!pagination.hasPrev}
                                className={`px-3 py-1 text-[12px] rounded-[6px] transition-all ${pagination.hasPrev ? "bg-primary-50 text-secondry-600 hover:bg-primary-100" : "bg-primary-50 text-primary-300 cursor-not-allowed"}`}>Previous</button>
                            <span className="text-[12px] text-primary-400">Page {pagination.currentPage} of {pagination.totalPages}</span>
                            <button type="button" onClick={() => setCurrentPage(p => p + 1)} disabled={!pagination.hasNext}
                                className={`px-3 py-1 text-[12px] rounded-[6px] transition-all ${pagination.hasNext ? "bg-primary-50 text-secondry-600 hover:bg-primary-100" : "bg-primary-50 text-primary-300 cursor-not-allowed"}`}>Next</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

PrescriptionMaker.displayName = "PrescriptionMaker";

export default PrescriptionMaker;
